/**
 * Portal record block (ADR-0010 S5 · decoupled in S5d) — edits ONE existing
 * row of a TABLE, bound by id. It loads the table's editable column spec
 * (`getTableSchema`) and the row (`getRow`, read-RLS gated), renders the
 * columns prefilled, and Save → `updateRow` / Delete → `deleteRow` (both
 * write-policy gated server side; a row the role may not write simply has no
 * effect → surfaced as an error).
 *
 * There is NO form in the loop: pages and forms are decoupled (the editor
 * configures the table + columns directly). The `row_id` is usually a binding
 * (`{{ $page.row }}`) seeded from the URL or a row action.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ApiError } from "@/lib/api";
import { portalApi } from "@/lib/api-portal";

import { PortalFileViewer } from "./PortalFileViewer";
import { Field, showWhenOk } from "./PortalFormBlock";
import { renderCellValue } from "./widgetUi";

export function PortalRecordBlock({
  appSlug,
  tableId,
  columns,
  rowId,
  allowDelete,
  layoutColumns = 1,
  readonly = false,
  onChanged,
}: {
  appSlug: string;
  tableId: string;
  /** Optional subset of column names to edit (empty = all of the table's). */
  columns: string[];
  rowId: string;
  allowDelete: boolean;
  /** Form pro A — lay the editable fields out on a 1- or 2-column grid. */
  layoutColumns?: number;
  /** Consultation mode: render a clean read-only detail (labels + values, a
   *  `file` column → inline viewer) instead of editable inputs. No Save/Delete. */
  readonly?: boolean;
  onChanged: () => void;
}) {
  const schemaQ = useQuery({
    queryKey: ["portal-table-schema", appSlug, tableId],
    queryFn: () => portalApi.getTableSchema(appSlug, tableId),
    retry: false,
    enabled: !!tableId,
  });
  const rowQ = useQuery({
    queryKey: ["portal-row", appSlug, tableId, rowId],
    queryFn: () => portalApi.getRow(appSlug, tableId, rowId),
    retry: false,
    enabled: !!tableId && !!rowId,
  });

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Seed the editable values from the loaded row (once per row load).
  useEffect(() => {
    if (rowQ.data?.row) setValues(rowQ.data.row);
  }, [rowQ.data]);

  if (!tableId) {
    return <p className="text-sm text-muted-foreground italic">Pick a table to edit.</p>;
  }
  if (!rowId) {
    return <p className="text-sm text-muted-foreground italic">No record selected.</p>;
  }
  if (schemaQ.isLoading || rowQ.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading record…</p>;
  }
  if (schemaQ.error || !schemaQ.data) return <p className="text-sm text-danger">Table unavailable.</p>;
  if (rowQ.error || !rowQ.data) return <p className="text-sm text-danger">Record not found.</p>;

  // Restrict to the configured column subset (if any), preserving table order.
  const wanted = new Set(columns ?? []);
  const fields = schemaQ.data.columns.filter(
    (c) => (wanted.size === 0 || wanted.has(c.name)) && showWhenOk(c.show_when, values),
  );
  // Consultation mode: a clean read-only detail (labels + values). A `file`
  // column renders the inline viewer; relations show their resolved label.
  if (readonly) {
    const relLabels = (values as { __rel?: Record<string, string | string[]> }).__rel;
    const cols = layoutColumns === 2 ? 2 : 1;
    const readValue = (f: (typeof fields)[number]) => {
      const v = values[f.name];
      if (f.widget === "file") {
        return v ? (
          <PortalFileViewer appSlug={appSlug} file={v} height={420} />
        ) : (
          <span className="text-sm text-muted-foreground/70">—</span>
        );
      }
      if (f.widget === "relation" || f.widget === "relation_many") {
        const lbl = relLabels?.[f.name];
        return (
          <span className="text-sm text-foreground">
            {Array.isArray(lbl) ? lbl.join(", ") : (lbl ?? renderCellValue(v))}
          </span>
        );
      }
      return <span className="text-sm text-foreground">{renderCellValue(v)}</span>;
    };
    return (
      <div
        className="grid gap-x-6 gap-y-4"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {fields.map((f) => (
          <div
            key={f.name}
            className="flex flex-col gap-1"
            style={f.widget === "file" ? { gridColumn: "1 / -1" } : undefined}
          >
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {f.label || f.name}
            </span>
            {readValue(f)}
          </div>
        ))}
      </div>
    );
  }

  const input =
    "w-full rounded-[var(--portal-radius)] border border-border bg-surface px-2.5 py-2 text-sm outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20";

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {};
      for (const f of fields) payload[f.name] = values[f.name] ?? null;
      await portalApi.updateRow(appSlug, tableId, rowId, payload);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : "save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await portalApi.deleteRow(appSlug, tableId, rowId);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : "delete failed");
    } finally {
      setBusy(false);
    }
  }

  const cols = layoutColumns === 2 ? 2 : 1;
  // __rel holds resolved relation labels: a string for a single relation, a
  // parallel list for relation_many (passed straight to the multi-picker).
  const rel = (values as { __rel?: Record<string, string | string[]> }).__rel;
  return (
    <div className="flex flex-col gap-3">
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {fields.map((f) => (
          // A `file` dropzone spans the full grid width (a half-column is cramped).
          <div key={f.name} style={f.widget === "file" ? { gridColumn: "1 / -1" } : undefined}>
            <Field
              field={f}
              value={values[f.name]}
              cls={input}
              onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))}
              onUpload={(file) => portalApi.uploadAppAttachment(appSlug, file)}
              onDownload={(v) => portalApi.downloadAttachment(appSlug, v.ref, v.filename)}
              loadOptions={(tableId, s) => portalApi.tableOptions(appSlug, tableId, s)}
              loadMembers={() =>
                portalApi.listMembers(appSlug).then((ms) => ms.map((m) => ({ id: m.id, label: m.name })))
              }
              initialLabel={rel?.[f.name] ?? null}
            />
          </div>
        ))}
      </div>
      {error && <p className="text-sm text-danger">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={save}
          className="w-fit rounded-[var(--portal-radius)] bg-[var(--portal-accent,hsl(var(--accent)))] px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "…" : "Save"}
        </button>
        {allowDelete && (
          <button
            type="button"
            disabled={busy}
            onClick={remove}
            className="w-fit rounded-[var(--portal-radius)] border border-danger px-4 py-2 text-sm font-medium text-danger transition hover:bg-danger/10 disabled:opacity-50"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
